/**
 * Date Utility Functions
 * Handles date formatting, parsing, and manipulation
 */

/**
 * Get today's date in YYYY-MM-DD format using device local time
 */
export const getTodayDateString = (): string => {
  const today = new Date();
  return formatDateToISO(today);
};

/**
 * Format a Date object to YYYY-MM-DD string
 */
export const formatDateToISO = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Parse YYYY-MM-DD string to Date object
 */
export const parseDateFromISO = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Check if a date string is today
 */
export const isToday = (dateString: string): boolean => {
  return dateString === getTodayDateString();
};

/**
 * Check if a date string is in the future
 */
export const isFutureDate = (dateString: string): boolean => {
  const date = parseDateFromISO(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date > today;
};

/**
 * Get date N days ago from today
 */
export const getDaysAgo = (daysAgo: number): string => {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return formatDateToISO(date);
};

/**
 * Generate array of date strings for last N days (including today)
 * Returns in reverse chronological order (most recent first)
 */
export const getLastNDays = (n: number): string[] => {
  const dates: string[] = [];
  for (let i = 0; i < n; i++) {
    dates.push(getDaysAgo(i));
  }
  return dates;
};

/**
 * Calculate days between two date strings
 */
export const daysBetween = (date1: string, date2: string): number => {
  const d1 = parseDateFromISO(date1);
  const d2 = parseDateFromISO(date2);
  const diffTime = Math.abs(d2.getTime() - d1.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Format date for display (e.g., "Today", "Yesterday", "Dec 26")
 */
export const formatDateForDisplay = (dateString: string): string => {
  if (isToday(dateString)) {
    return 'Today';
  }

  const yesterday = getDaysAgo(1);
  if (dateString === yesterday) {
    return 'Yesterday';
  }

  const date = parseDateFromISO(dateString);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
};
